import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.gridlayer.googlemutant";

const GoogleMapsLayer = ({ type, isTrafficViewEnabled }) => {
  const map = useMap();
  const layersRef = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  console.log('updated code testing===>')


  useEffect(() => {
    if (!map) return;

    // Capture the ref value at the beginning to avoid stale closure issues
    const currentLayersRef = layersRef.current;

    const setupWithGoogle = () => {
      console.log('Setting up Google Maps...');
      setIsLoading(true);
      setHasError(false);
      
      const baseType = type === "hybrid" ? "satellite" : type;
      
      try {
        const newBaseLayer = L.gridLayer.googleMutant({
          type: baseType,
          detectRetina: true,
          maxZoom: 21,
        });
        
        // Add the base layer
        newBaseLayer.addTo(map);
        layersRef.current["base"] = newBaseLayer;
        
        // Add labels layer for hybrid view
        if (type === "hybrid" && process.env.REACT_APP_GOOGLE_API_KEY) {
          try {
            const labelsLayer = L.tileLayer(
              `https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
              {
                maxZoom: 21,
                tileSize: 256,
                detectRetina: true,
                attribution: '&copy; <a href="https://www.google.com/maps">Google Maps</a>',
              }
            );
            labelsLayer.addTo(map);
            layersRef.current["labels"] = labelsLayer;
          } catch (error) {
            console.log("Labels layer failed:", error);
          }
        }

        // Add traffic layer if enabled
        if (isTrafficViewEnabled && process.env.REACT_APP_GOOGLE_API_KEY) {
          try {
            const trafficLayerUrl =
              type === "hybrid"
                ? `https://mt1.google.com/vt/lyrs=s,h,traffic&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
                : `https://mt1.google.com/vt/lyrs=m,traffic&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
            const trafficLayer = L.tileLayer(trafficLayerUrl, {
              maxZoom: 21,
              tileSize: 256,
              detectRetina: true,
              attribution: '&copy; <a href="https://www.google.com/maps">Google Maps</a>',
            });
            trafficLayer.addTo(map);
            layersRef.current["traffic"] = trafficLayer;
          } catch (error) {
            console.log("Traffic layer failed:", error);
          }
        }
        
        setIsLoading(false);
        console.log('Google Maps loaded successfully');
      } catch (error) {
        console.error("Failed to setup Google Maps:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    let intervalId = null;
    let timeoutId = null;
    let didSetupGoogle = false;

    const waitForGoogle = (timeoutMs = 8000, intervalMs = 200) =>
      new Promise((resolve, reject) => {
        if (window.google && window.google.maps) return resolve(true);
        intervalId = setInterval(() => {
          if (window.google && window.google.maps) {
            resolve(true);
          }
        }, intervalMs);
        timeoutId = setTimeout(() => reject(new Error("Google not ready")), timeoutMs);
      });

    // Check if Google Maps is already available
    if (window.google && window.google.maps) {
      setupWithGoogle();
    } else {
      // Wait for Google Maps to load - NO FALLBACK MAPS
      waitForGoogle()
        .then(() => {
          if (!map || didSetupGoogle) return;
          didSetupGoogle = true;
          setupWithGoogle();
        })
        .catch(() => {
          console.error('Google Maps failed to load completely');
          setHasError(true);
          setIsLoading(false);
          // NO FALLBACK - just show error state
        })
        .finally(() => {
          if (intervalId) clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
        });
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      // Clean up layers using the captured ref value
      Object.keys(currentLayersRef).forEach((key) => {
        const layer = currentLayersRef[key];
        if (layer && map.hasLayer(layer)) {
          map?.removeLayer?.(layer);
        }
      });
    };
  }, [map, type, isTrafficViewEnabled]);

  // Show loading skeleton or error state instead of any fallback map
  if (isLoading) {
    return (
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backgroundSize: '200% 100%',
          animation: 'loading 1.5s infinite'
        }}
      >
        <div 
          style={{
            width: '50px',
            height: '50px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}
        />
        <div style={{ color: '#666', fontSize: '16px', fontWeight: '500' }}>
          Loading Map...
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes loading {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>
      </div>
    );
  }

  if (hasError) {
    return (
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          color: '#6c757d'
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗺️</div>
        <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '10px' }}>
          Map Unavailable
        </div>
        <div style={{ fontSize: '14px', textAlign: 'center', maxWidth: '300px' }}>
          Unable to load Google Maps. Please check your internet connection and try again.
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleMapsLayer;